import { c as _c } from "react/compiler-runtime";
import { NextPage } from 'next';
import { SSRConfig, appWithTranslation } from 'next-i18next/pages';
import nextI18NextConfig from '../../next-i18next.config';
import NextApp from 'next/app';
import type { AppContext, AppInitialProps, AppProps } from 'next/app';
import { Router } from 'next/router';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import '../../styles/nprogress.css';
import { ReactElement, ReactNode, useEffect } from 'react';
import { avertaFont, FontVariableEnum, interFont } from 'shared/constants/fonts';
import DashboardLayout from 'shared/layout/components/dashboard-layout';
import { RootProviders } from 'shared/providers/root-providers';
import '../../styles/globals.css';
import { DeviceTypeEnum, MetaDataFromServerInterface } from 'shared/hooks/use-metadata-from-server';
import { getServerDeviceType } from 'shared/utils/device/get-server-device-type';
// import { VersionMonitor } from 'shared/components/version-monitor'

export type NextPageWithLayout<P = {}, R = P> = NextPage<PageProps> & {
  getLayout: (page: ReactElement<any>, browserTabTitle: PageProps['browserTabTitle']) => ReactNode;
  getSecondaryLayout: (page: ReactElement<P>) => ReactElement<R>;
};
export interface PageProps {
  browserTabTitle: string;
}
type AppPropsWithLayout = AppProps<PageProps> & MetaDataFromServerInterface & {
  pageProps: SSRConfig;
} & {
  Component: NextPageWithLayout;
};
const getDefaultPageLayout = (page: ReactElement<any>, browserTabTitle: PageProps['browserTabTitle']) => <DashboardLayout browserTabTitle={browserTabTitle}>{page}</DashboardLayout>;
function App(t0) {
  const $ = _c(14);
  const {
    Component,
    pageProps,
    hostOnServer,
    serverDeviceType
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useEffect(_temp3, t1);
  const getLayout = Component.getLayout ?? getDefaultPageLayout;
  const getSecondaryLayout = Component.getSecondaryLayout ?? _temp4;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <style jsx={true} global={true}>{`
        :root {
          ${FontVariableEnum.Inter}: ${interFont.style.fontFamily};
          ${FontVariableEnum.Averta}: ${avertaFont.style.fontFamily};
        }
      `}</style>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== Component || $[3] !== pageProps) {
    t3 = <Component {...pageProps} />;
    $[2] = Component;
    $[3] = pageProps;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== getLayout || $[6] !== getSecondaryLayout || $[7] !== pageProps.browserTabTitle || $[8] !== t3) {
    t4 = getLayout(getSecondaryLayout(t3), pageProps.browserTabTitle);
    $[5] = getLayout;
    $[6] = getSecondaryLayout;
    $[7] = pageProps.browserTabTitle;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== hostOnServer || $[11] !== serverDeviceType || $[12] !== t4) {
    t5 = <>{t2}<RootProviders hostOnServer={hostOnServer} serverDeviceType={serverDeviceType}>{t4}</RootProviders></>;
    $[10] = hostOnServer;
    $[11] = serverDeviceType;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
function _temp4(page) {
  return page;
}
function _temp3() {
  const handleRouteStart = _temp;
  const handleRouteDone = _temp2;
  Router.events.on("routeChangeStart", handleRouteStart);
  Router.events.on("routeChangeComplete", handleRouteDone);
  Router.events.on("routeChangeError", handleRouteDone);
  return () => {
    Router.events.off("routeChangeStart", handleRouteStart);
    Router.events.off("routeChangeComplete", handleRouteDone);
    Router.events.off("routeChangeError", handleRouteDone);
  };
}
function _temp2() {
  return NProgress.done();
}
function _temp() {
  return NProgress.start();
}
App.getInitialProps = async (context: AppContext): Promise<MetaDataFromServerInterface & AppInitialProps> => {
  const ctx = await NextApp.getInitialProps(context);
  const {
    req,
    res
  } = context.ctx;
  const hostOnServer = req?.headers?.host || null;
  const serverDeviceType = req ? getServerDeviceType(req.headers['user-agent']) : DeviceTypeEnum.Unknown;
  if (req && res && req.url) {
    if (res.statusCode === 404) {
      const accept = req.headers['accept'] || '';
      const isHtmlRequest = accept.includes('text/html');

      // Only redirect actual page requests, not Next.js data fetching
      if (isHtmlRequest) {
        context.ctx.res?.writeHead(302, {
          Location: '/dashboard/not-found'
        });
        context.ctx.res?.end();
      }
    }
  }
  return {
    ...ctx,
    hostOnServer,
    serverDeviceType
  };
};
export default appWithTranslation(App, nextI18NextConfig);